import { useState } from "react";
import {
  ArrowRightIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import type { UseFilterBuilderReturn } from "../hooks/useFilterBuilder";
import {
  containsKeywordDelimiter,
  parsePastedKeywords,
} from "../parsePastedList";
import NotificationMethodCheckboxes from "@/ui-components/NotificationMethodCheckboxes";
import DismissibleTip from "@/shared/components/DismissibleTip";
import { useIsDeskUser } from "@/hooks/useIsDeskUser";
import DeskPublishFields from "./DeskPublishFields";
import PublishConfirmDialog from "./PublishConfirmDialog";

interface QueryBuilderPaneProps {
  builder: UseFilterBuilderReturn;
  onCancel?: () => void;
  /**
   * Render only the query composition, for use inside the watchlist builder.
   *
   * The name, notifications and save button are the host form's — combining is
   * a way of describing one watchlist, not a second thing being saved, so it
   * must not bring a second set of those along with it.
   */
  embedded?: boolean;
  /**
   * Inline only — swap the keyword controls for a way into the full builder.
   * Absent, the keyword controls render as before, which is what the
   * full-screen builder itself needs.
   */
  onOpenAdvanced?: () => void;
}

export default function QueryBuilderPane({
  builder,
  onCancel,
  embedded = false,
  onOpenAdvanced,
}: QueryBuilderPaneProps) {
  // Chip-based keyword state
  const [keywordInput, setKeywordInput] = useState("");
  const [isAdvancedMode, setIsAdvancedMode] = useState(
    !!builder.searchKeyword.trim()
  );
  const [keywordChips, setKeywordChips] = useState<string[]>(() => {
    // Parse existing searchKeyword into chips if it's a simple flat list
    const kw = builder.searchKeyword.trim();
    if (!kw) return [];
    // Try to split by OR (simple flat case)
    // Try to parse simple flat expressions (no nested grouping)
    const hasNesting = (kw.match(/\(/g) || []).length > 1;
    if (!hasNesting) {
      return kw
        .split(/\s+OR\s+/i)
        .map((s) => s.trim().replace(/^[("]+|[)"]+$/g, ""))
        .filter(Boolean);
    }
    // Complex expression — start in advanced mode
    return [];
  });
  const [chipOperator, setChipOperator] = useState<"OR" | "AND">("OR");

  // Sync chips to builder.searchKeyword
  const syncChipsToKeyword = (chips: string[], op: "OR" | "AND") => {
    if (chips.length === 0) {
      builder.setSearchKeyword("");
      return;
    }
    const joined = chips
      .map((c) => (c.includes(" ") ? `(${c})` : c))
      .join(` ${op} `);
    builder.setSearchKeyword(joined);
  };

  const addChip = () => {
    const trimmed = keywordInput.trim();
    if (trimmed && !keywordChips.includes(trimmed)) {
      const next = [...keywordChips, trimmed];
      setKeywordChips(next);
      syncChipsToKeyword(next, chipOperator);
      setKeywordInput("");
    }
  };

  const addChipsBulk = (values: string[]) => {
    if (values.length === 0) return;
    const seen = new Set(keywordChips);
    const next = [...keywordChips];
    for (const v of values) {
      if (!seen.has(v)) {
        seen.add(v);
        next.push(v);
      }
    }
    setKeywordChips(next);
    syncChipsToKeyword(next, chipOperator);
    setKeywordInput("");
  };

  const removeChip = (chip: string) => {
    const next = keywordChips.filter((c) => c !== chip);
    setKeywordChips(next);
    syncChipsToKeyword(next, chipOperator);
  };

  const toggleChipOperator = () => {
    const next = chipOperator === "OR" ? "AND" : "OR";
    setChipOperator(next);
    syncChipsToKeyword(keywordChips, next);
  };

  /**
   * Publishing to the Newsquawk library, as the other three builders offer it.
   *
   * Not embedded: the host form shows its own there and hands them to the
   * hook. Not on an edit of the desk member's own list either — the server
   * ignores owner_type on PATCH, so the tick would do nothing.
   */
  const isDeskUser = useIsDeskUser();
  const showDeskFields =
    isDeskUser && !embedded && (!builder.isEditing || builder.isNewsquawkOwned);
  const [confirmingPublish, setConfirmingPublish] = useState(false);

  /** Publishing is irreversible and live on save — ask on the way in only. */
  const handleSave = () => {
    if (isDeskUser && builder.publishToLibrary && !builder.isNewsquawkOwned) {
      setConfirmingPublish(true);
      return;
    }
    builder.handleSubmit();
  };

  return (
    <div
      className={clsx(
        embedded
          ? "flex flex-col"
          : "h-full flex flex-col border-l border-gray-700/50 bg-gray-900/50 overflow-hidden"
      )}
    >
      {/* Header — name input only */}
      <div
        className={clsx(
          "px-4 py-3 border-b border-gray-700/30",
          embedded && "hidden"
        )}
      >
        <input
          type="text"
          value={builder.queryName}
          onChange={(e) => builder.setQueryName(e.target.value)}
          placeholder="Name this watchlist"
          className="w-full px-3 py-2.5 text-sm bg-gray-800 text-gray-100 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-500"
        />

        {/* What to do here, once.
            The modal opens with a watchlist already in the query and no
            indication of what is expected next — a form someone else
            half-filled. Same dismissible tip as the widget filter panel, so
            the pattern is one someone has likely met already. */}
        <DismissibleTip id="combined-watchlist-builder" className="mt-2">
          This matches anything in the lists below. Tick more watchlists on the
          left to widen it, or add your own keywords underneath.
        </DismissibleTip>

        {builder.formErrors.categories && (
          <div className="mt-1.5 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {builder.formErrors.categories}
          </div>
        )}
        {builder.formErrors.keywords && (
          <div className="mt-1.5 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {builder.formErrors.keywords}
          </div>
        )}
      </div>

      {/* Body (scrollable) */}
      <div
        className={clsx(
          embedded ? "space-y-3" : "space-y-5",
          embedded
            ? "px-0 py-0"
            : "flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent px-4 py-4"
        )}
      >
        {/* Straight under the name, where the other builders put it. */}
        {showDeskFields && (
          <DeskPublishFields
            publish={builder.publishToLibrary}
            onPublishChange={builder.setPublishToLibrary}
            description={builder.description}
            onDescriptionChange={builder.setDescription}
            whenToUse={builder.whenToUse}
            onWhenToUseChange={builder.setWhenToUse}
            categories={builder.assetClasses}
            onCategoriesChange={builder.setAssetClasses}
            alreadyPublished={builder.isNewsquawkOwned}
          />
        )}

        {/* Query preview */}
        <div>
          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Query
          </div>
          <QueryPreview builder={builder} />
        </div>

        {/* Inline, the way on rather than the last few controls.
            Keywords, AND/OR and the boolean expression are the advanced half of
            this form, and inline they sit in a column with no room for the
            library they operate on — a flat, unsearchable list of every
            watchlist, with tag lists missing altogether. Offering the builder
            that does have room beats reproducing a worse copy of it here, so
            this slot points there and the simple case stays simple. */}
        {embedded && onOpenAdvanced ? (
          <div className="rounded border border-gray-700/70 bg-gray-800/40 px-3 py-2.5">
            <p className="text-xs text-gray-400">
              Need keywords, AND/OR or the full Newsquawk library?
            </p>
            <button
              type="button"
              onClick={onOpenAdvanced}
              className="mt-1 inline-flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-xs font-semibold text-squawk-blue transition-colors hover:text-squawk-blue/80"
            >
              Open Advanced Builder
              <ArrowRightIcon className="size-3" />
            </button>
          </div>
        ) : (
          /* Keyword input — chip mode or advanced textarea */
          <div>
            {/* Says out loud what the field is for.
                A watchlist assembled from lists and tags covers the market but
                not always the exact story someone is watching, and the fix is
                one line away — but "Additional keywords" as a grey uppercase
                label reads as a section heading rather than as an invitation.
                Blue because that is already this pane's colour for "you can do
                more here", matching Advanced mode beside it. */}
            <p className="mb-2 text-xs leading-snug text-squawk-blue">
              Need a few extra terms? Add them here to catch anything the lists
              miss.
            </p>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Additional keywords
              </div>
              <button
                onClick={() => setIsAdvancedMode(!isAdvancedMode)}
                className="text-2xs text-squawk-blue hover:text-squawk-blue/80 transition-colors"
              >
                {isAdvancedMode ? "Simple mode" : "Advanced mode"}
              </button>
            </div>

            {isAdvancedMode ? (
              <>
                <textarea
                  value={builder.searchKeyword}
                  onChange={(e) => builder.setSearchKeyword(e.target.value)}
                  placeholder="(trump AND powell) OR (iran AND strait) OR tariffs"
                  className="w-full px-3 py-2.5 text-sm bg-gray-800 text-gray-100 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-500 min-h-[60px] resize-y"
                  rows={2}
                />
                <div className="text-2xs text-gray-500 mt-1.5">
                  Use parentheses to group terms,{" "}
                  <code className="bg-gray-700 px-1 rounded text-gray-300">
                    OR
                  </code>{" "}
                  to match any,{" "}
                  <code className="bg-gray-700 px-1 rounded text-gray-300">
                    AND
                  </code>{" "}
                  to require all.
                </div>
              </>
            ) : (
              <>
                <div className="flex gap-1.5">
                  <input
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addChip();
                      }
                    }}
                    onPaste={(e) => {
                      const text = e.clipboardData.getData("text");
                      if (!containsKeywordDelimiter(text)) return;
                      e.preventDefault();
                      addChipsBulk(parsePastedKeywords(text));
                    }}
                    placeholder="Add keyword..."
                    className="flex-1 px-3 py-1.5 text-sm bg-gray-800 text-gray-100 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue placeholder:text-gray-600"
                  />
                  <button
                    onClick={addChip}
                    className="px-2 py-1.5 text-xs rounded bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors"
                  >
                    <PlusIcon className="size-3.5" />
                  </button>
                </div>
                {keywordChips.length > 0 && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    {keywordChips.map((chip, i) => (
                      <span key={chip} className="contents">
                        {i > 0 && (
                          <button
                            onClick={toggleChipOperator}
                            className="px-1.5 py-0.5 text-2xs font-bold rounded border border-squawk-blue/50 bg-squawk-blue/20 text-squawk-blue hover:bg-squawk-blue/30 transition-colors cursor-pointer"
                            title={`Click to toggle to ${chipOperator === "OR" ? "AND" : "OR"}`}
                          >
                            {chipOperator}
                          </button>
                        )}
                        <span className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded bg-purple-900/20 text-purple-300 border border-purple-800/30 text-xs">
                          {chip}
                          <button
                            onClick={() => removeChip(chip)}
                            className="p-0.5 text-purple-400 hover:text-purple-200"
                          >
                            <XMarkIcon className="size-2.5" />
                          </button>
                        </span>
                      </span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Keyword size */}
        {builder.hasKeywords && (
          <div
            className={clsx(
              "rounded border px-3 py-2 text-xs",
              builder.keywordsSize.ok
                ? "border-gray-700 text-gray-400"
                : "border-red-500/40 bg-red-500/10 text-red-300"
            )}
          >
            {builder.keywordsSize.count}/{builder.MAX_KEYWORDS_COUNT} keywords
            {" · "}
            {builder.keywordsSize.totalChars}/{builder.MAX_KEYWORDS_TOTAL_CHARS}{" "}
            chars
          </div>
        )}

        {/* Highlight colour — combined watchlists highlight as well as
            filter. Same control the watchlist builder uses. */}
        <div className={clsx("flex items-center justify-between", embedded && "hidden")}>
          <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">
            Highlight color
          </span>
          <div className="flex items-center gap-2">
            <label className="relative cursor-pointer">
              <span
                className="block size-7 rounded border border-gray-600 hover:border-squawk-blue transition-colors"
                style={{ backgroundColor: builder.displayColor }}
              />
              <input
                type="color"
                value={builder.displayColor}
                onChange={(e) => builder.setColor(e.target.value)}
                className="absolute inset-0 opacity-0 cursor-pointer size-7"
                aria-label="Highlight color"
              />
            </label>
            <input
              type="text"
              value={builder.displayColor}
              onChange={(e) => {
                const v = e.target.value;
                if (/^#[0-9a-fA-F]{0,6}$/.test(v)) builder.setColor(v);
              }}
              maxLength={7}
              className="w-20 px-2 py-1 text-xs font-mono bg-gray-800 text-gray-300 border border-gray-700 rounded focus:outline-none focus:border-squawk-blue"
            />
          </div>
        </div>
        {builder.hasLibraryOverride && !embedded && (
          <button
            type="button"
            onClick={builder.resetLibraryLook}
            className="-mt-2 cursor-pointer border-0 bg-transparent p-0 text-xs text-gray-500 transition-colors hover:text-squawk-blue"
          >
            Reset to Newsquawk&apos;s colour
          </button>
        )}

        {/* Notifications */}
        <div className={clsx(embedded && "hidden")}>
          <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">
            Notifications
          </div>
          <NotificationMethodCheckboxes
            value={builder.notifications}
            onChange={builder.setNotifications}
            comingSoonMethods={["sound", "popup"]}
            variant="pane"
          />
        </div>
      </div>

      {/* Footer */}
      <div
        className={clsx(
          "px-4 py-3 border-t border-gray-700/30 flex flex-col sm:flex-row sm:items-center gap-2",
          builder.isEditing ? "sm:justify-between" : "sm:justify-end",
          embedded && "hidden"
        )}
      >
        {builder.isEditing && (
          <button
            onClick={builder.handleDelete}
            disabled={builder.isDeleting}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded border border-red-800/50 text-red-400 hover:bg-red-900/20 hover:text-red-300 transition-colors disabled:opacity-50 order-3 sm:order-1"
          >
            {builder.isDeleting ? "Deleting..." : "Delete"}
          </button>
        )}
        {/* What the save will do, said before it happens — see the same note
            in WatchlistBuilderPane. */}
        {builder.basedOn && (
          <p className="order-2 text-xs text-gray-500 sm:order-1 sm:mr-auto">
            {builder.copyUnchanged
              ? builder.libraryLookable
                ? "Change its colour to keep it for you, or its name, lists, tags or alerts to save your own version."
                : `Change something to save your own version. To use “${builder.basedOn}” as it is, pick it from any widget's filter or highlight menu.`
              : builder.lookOnly
                ? `Only for you — “${builder.basedOn}” itself doesn't change.`
                : builder.libraryContentChanged
                  ? "You've changed its name, lists, tags or alerts, so this saves a new watchlist of your own."
                  : null}
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-2 order-1 sm:order-2">
          {onCancel && (
            <button
              onClick={onCancel}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded bg-gray-700/50 border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-gray-100 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={
              builder.isSubmitting ||
              !builder.queryName.trim() ||
              builder.copyUnchanged
            }
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium rounded bg-squawk-blue text-white hover:bg-squawk-blue/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {builder.isSubmitting
              ? "Saving..."
              : builder.isEditing
                ? "Save changes"
                : builder.basedOn
                  ? builder.libraryLookable && !builder.libraryContentChanged
                    ? "Save colour"
                    : "Save as new watchlist"
                  : "Save watchlist"}
          </button>
        </div>
      </div>

      <PublishConfirmDialog
        open={confirmingPublish}
        listName={builder.queryName.trim() || "Untitled watchlist"}
        onCancel={() => setConfirmingPublish(false)}
        onConfirm={() => {
          setConfirmingPublish(false);
          builder.handleSubmit();
        }}
      />
    </div>
  );
}

// ── Query Preview with visual grouping ─────────────────────────────────

function QueryPreview({ builder }: { builder: UseFilterBuilderReturn }) {
  const hasAnything = builder.hasKeywords || builder.hasWatchlistsOrTags;

  if (!hasAnything) {
    return (
      <div className="bg-gray-800/50 rounded px-3 py-2.5 text-sm text-gray-500">
        Pick watchlists above to combine them
      </div>
    );
  }

  return (
    <div className="bg-gray-800/50 rounded px-2.5 py-2 space-y-1.5">
      {/* Keyword group */}
      {builder.hasKeywords && (
        <div className="border border-purple-800/30 rounded px-2 py-1.5">
          <div className="text-2xs font-medium text-purple-400/70 uppercase tracking-wider mb-1">
            Keywords
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {builder.searchKeyword.trim() && (
              <QueryChip
                label={builder.searchKeyword.trim()}
                accent="border-l-purple-400"
                onRemove={() => builder.setSearchKeyword("")}
              />
            )}
            {builder.selectedGrouped.keywordOnly.map((p, i) => (
              <span key={p.id} className="contents">
                {(i > 0 || builder.searchKeyword.trim()) && (
                  <OperatorToggle
                    value={builder.keywordWatchlistOp}
                    onChange={builder.setKeywordWatchlistOp}
                  />
                )}
                <QueryChip
                  label={p.name}
                  accent="border-l-purple-400"
                  onRemove={() => builder.togglePortfolio(p.id)}
                />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Joiner between keywords and watchlists/tags */}
      {builder.hasKeywords && builder.hasWatchlistsOrTags && (
        <div className="flex justify-center">
          <OperatorToggle
            value={builder.keywordOp}
            onChange={builder.setKeywordOp}
          />
        </div>
      )}

      {/* Ticker Watchlists group */}
      {builder.hasTickerWatchlists && (
        <div className="border border-blue-800/30 rounded px-2 py-1.5">
          <div className="text-2xs font-medium text-blue-400/70 uppercase tracking-wider mb-1">
            Watchlists
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {builder.selectedGrouped.tickerOnly.map((p, i) => (
              <span key={p.id} className="contents">
                {i > 0 && (
                  <span className="text-xs text-gray-400 font-medium px-0.5">
                    OR
                  </span>
                )}
                <QueryChip
                  label={p.name}
                  accent="border-l-blue-400"
                  onRemove={() => builder.togglePortfolio(p.id)}
                />
              </span>
            ))}
          </div>
        </div>
      )}

      {/* AND/OR between tickers and tags */}
      {builder.hasTickerWatchlists && builder.hasTags && (
        <div className="flex justify-center">
          <OperatorToggle
            value={builder.filterMode}
            onChange={builder.setFilterMode}
          />
        </div>
      )}

      {/* Tags group */}
      {builder.hasTags && (
        <div className="border border-green-800/30 rounded px-2 py-1.5">
          <div className="text-2xs font-medium text-green-400/70 uppercase tracking-wider mb-1">
            Tags
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {builder.selectedCategories.map((c, i) => (
              <span key={`c-${c.id}`} className="contents">
                {i > 0 && (
                  <span className="text-xs text-gray-400 font-medium px-0.5">
                    OR
                  </span>
                )}
                <QueryChip
                  label={c.title}
                  accent="border-l-green-400"
                  onRemove={() => builder.toggleCategory(c)}
                />
              </span>
            ))}

            {builder.selectedTagWatchlistObjects.map((tw, i) => (
              <span key={`tw-${tw.id}`} className="contents">
                {(i > 0 || builder.selectedCategories.length > 0) && (
                  <span className="text-xs text-gray-400 font-medium px-0.5">
                    OR
                  </span>
                )}
                <QueryChip
                  label={tw.name}
                  accent="border-l-green-400"
                  onRemove={() => builder.toggleTagWatchlist(tw.id)}
                />
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Query Chip ──────────────────────────────────────────────────────────

function QueryChip({
  label,
  accent,
  onRemove,
}: {
  label: string;
  accent: string;
  onRemove: () => void;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-r bg-gray-700/60 border border-gray-600/40 border-l-2 ${accent} text-xs text-gray-100`}
    >
      <span>{label}</span>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="p-0.5 text-gray-400 hover:text-gray-200 transition-colors"
      >
        <XMarkIcon className="size-3" />
      </button>
    </span>
  );
}

// ── Operator Toggle ─────────────────────────────────────────────────────

function OperatorToggle({
  value,
  onChange,
}: {
  value: "AND" | "OR";
  onChange: (v: "AND" | "OR") => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(value === "AND" ? "OR" : "AND")}
      className="px-2 py-0.5 text-2xs font-bold rounded border border-squawk-blue/50 bg-squawk-blue/20 text-squawk-blue hover:bg-squawk-blue/30 transition-colors cursor-pointer whitespace-nowrap focus:outline-none"
      title={`Click to toggle to ${value === "AND" ? "OR" : "AND"}`}
    >
      {value}&#x25BE;
    </button>
  );
}
